import { FC } from 'react'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { Avatar } from '@mui/material'

/**
 * GitLab-style identicon avatar: the initials on a background color that is
 * deterministically derived from a hash of the name. The same user therefore
 * always gets the same, recognizable color.
 */

// Curated, accessible palette — every entry clears WCAG AA (>= 4.5:1) against
// white so the initials stay legible at small sizes. The cyan and orange were
// darkened from their brand tints (which sat around 3:1) to reach that bar.
const AVATAR_COLORS = [
  '#31679B', // brand blue
  '#2C4A69',
  '#00734D', // green
  '#0E5186',
  '#7C3AED', // purple
  '#C24242', // red
  '#C2571F', // orange (darkened for contrast)
  '#1E6844',
  '#2E6B7D', // cyan (darkened for contrast)
  '#B45309',
  '#6D28D9',
  '#9D174D',
] as const

const hashString = (value: string): number => {
  // Multiplicative rolling hash mod a large prime — deterministic per string,
  // avoids bitwise ops (and stays within safe-integer range).
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 1000000007
  }
  return hash
}

const colorForSeed = (seed: string): string =>
  AVATAR_COLORS[hashString(seed || '?') % AVATAR_COLORS.length]

/**
 * Two-letter initials: first letters of the first and last word of the name.
 */
const initialsFor = (name?: string): string => {
  const trimmed = (name ?? '').trim()
  if (trimmed) {
    const parts = trimmed.split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return '?'
}

export const UserAvatar: FC<{ name?: string; size?: number }> = ({ name, size = 32 }) => {
  const initials = initialsFor(name)

  return (
    <Avatar
      alt={name || initials}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: '#fff',
        bgcolor: colorForSeed((name || '?').toLowerCase()),
      }}
    >
      {/* No name known: a person icon instead of "?". */}
      {initials === '?' ? <PersonOutlineIcon sx={{ fontSize: size * 0.6 }} /> : initials}
    </Avatar>
  )
}
